/* Sham Cart Prefill - builds a cart from a link, then bounces to /cart.
   One implementation, many consumers. Everything that differs between
   consumers is a CONSUMERS entry below; the orchestration and the
   snapshot-and-restore path exist exactly once. */

(function (global) {
  'use strict';

  // Selling plan group 78023622960 "Subscribe and Save". Re-derive with:
  //   query { sellingPlanGroup(id: "gid://shopify/SellingPlanGroup/78023622960") {
  //     sellingPlans(first: 10) { nodes { id name } } } }
  var PLAN_IDS = {
    '1': '690388500784', // Every 1 Week
    '2': '690388468016', // Every 2 Weeks
    '3': '690388435248', // Every 3 Weeks
    '4': '690388402480', // Every 4 Weeks
  };

  var MAX_LINES = 50;
  var MAX_QUANTITY = 100;
  var TOKEN_MAX_LENGTH = 32;

  // A third consumer is an entry here plus a page template - never another
  // copy of the orchestration. Param names are deliberately identical across
  // consumers: reorder's i/p/src/o are frozen and campaign reuses them, so
  // there is one URL contract to document rather than two.
  var CONSUMERS = {
    reorder: {
      itemsParam: 'i',
      planParam: 'p',
      sourceParam: 'src',
      refParam: 'o',
      sourceAttribute: 'reorder_source',
      refAttribute: 'reorder_order',
      defaultSource: 'reorder_unknown',
      clearAttributes: ['campaign_source'],
      droppedParam: 'ro_dropped',
      redirect: '/cart',
    },
    campaign: {
      itemsParam: 'i',
      planParam: 'p',
      sourceParam: 'src',
      refParam: null,
      sourceAttribute: 'campaign_source',
      refAttribute: null,
      defaultSource: 'campaign_unknown',
      clearAttributes: ['reorder_source', 'reorder_order'],
      droppedParam: 'pf_dropped',
      redirect: '/cart',
    },
  };

  function parseItems(raw) {
    if (typeof raw !== 'string' || raw === '') return [];

    var order = [];
    var byId = {};

    raw.split(',').forEach(function (chunk) {
      var parts = chunk.split(':');
      if (parts.length !== 2) return;

      var id = parts[0].trim();
      var quantityRaw = parts[1].trim();
      if (!/^\d{6,20}$/.test(id)) return;

      // Klaviyo (and other template engines) can render a whole number as a
      // float, e.g. "2.0" or "2.00". Accept that as a plain integer, but
      // only when the fractional part is all zeros - a genuine fraction like
      // "2.7" is meaningless here and silently rounding it would be worse
      // than dropping the line. The integer part is still capped at 1-3
      // digits, so this cannot be used to smuggle in an absurdly long number.
      var quantityMatch = /^(\d{1,3})(?:\.0+)?$/.exec(quantityRaw);
      if (!quantityMatch) return;

      var quantity = parseInt(quantityMatch[1], 10);
      if (quantity < 1 || quantity > MAX_QUANTITY) return;

      if (byId[id]) {
        byId[id].quantity = Math.min(byId[id].quantity + quantity, MAX_QUANTITY);
        return;
      }
      if (order.length >= MAX_LINES) return;
      byId[id] = { id: id, quantity: quantity };
      order.push(byId[id]);
    });

    return order;
  }

  function parsePlan(raw) {
    if (typeof raw !== 'string') return null;
    var key = raw.trim();
    return Object.prototype.hasOwnProperty.call(PLAN_IDS, key) ? PLAN_IDS[key] : null;
  }

  function sanitiseToken(raw, maxLength) {
    if (typeof raw !== 'string') return '';
    return raw.replace(/[^A-Za-z0-9_-]/g, '').slice(0, maxLength);
  }

  function resolveConsumer(name) {
    if (typeof name !== 'string') return null;
    var key = name.trim();
    return Object.prototype.hasOwnProperty.call(CONSUMERS, key) ? CONSUMERS[key] : null;
  }

  function buildAttributes(config, source, ref) {
    // Attribution is last touch. cart/clear.js empties line items and leaves
    // attributes behind, so without the erases below a cart could carry both
    // a reorder_source and a campaign_source and neither read would be
    // honest. Shopify deletes an attribute written as an empty string.
    var attributes = {};
    attributes[config.sourceAttribute] = source || config.defaultSource;
    if (config.refAttribute) attributes[config.refAttribute] = ref || '';
    config.clearAttributes.forEach(function (key) {
      attributes[key] = '';
    });
    return attributes;
  }

  function redirectTarget(config, droppedCount) {
    var count = typeof droppedCount === 'number' && droppedCount > 0 ? droppedCount : 0;
    if (!count) return config.redirect;
    return config.redirect + '?' + config.droppedParam + '=' + count;
  }

  global.ShamCartPrefill = {
    parseItems: parseItems,
    parsePlan: parsePlan,
    sanitiseToken: sanitiseToken,
    resolveConsumer: resolveConsumer,
    buildAttributes: buildAttributes,
    redirectTarget: redirectTarget,
    TOKEN_MAX_LENGTH: TOKEN_MAX_LENGTH,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = global.ShamCartPrefill;
  }
})(typeof window !== 'undefined' ? window : globalThis);

/* Sham Cart Prefill - custom element */

if (typeof HTMLElement !== 'undefined') {
class ShamCartPrefill extends HTMLElement {
  connectedCallback() {
    this.states = {
      building: this.querySelector('[data-prefill-state="building"]'),
      error: this.querySelector('[data-prefill-state="error"]'),
    };
    this.errorMessage = this.querySelector('[data-prefill-error-message]');
    // Copy is data, not code. Each consumer's page template supplies its own,
    // which is why a campaign error can never mention reorder.
    this.copy = {
      unreadable: this.dataset.errorUnreadable || '',
      noneAvailable: this.dataset.errorNoneAvailable || '',
      unexpected: this.dataset.errorUnexpected || '',
      restored: this.dataset.outcomeRestored || '',
      restoreFailed: this.dataset.outcomeRestoreFailed || '',
    };
    this.run();
  }

  async run() {
    const api = window.ShamCartPrefill;
    const config = api.resolveConsumer(this.dataset.consumer);

    if (!config) {
      // A misconfigured section, not a bad link. Say the least misleading
      // thing available and touch nothing.
      console.error('Cart prefill: unknown consumer', this.dataset.consumer);
      this.fail(this.copy.unreadable, false);
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const items = api.parseItems(params.get(config.itemsParam));
    const sellingPlan = api.parsePlan(params.get(config.planParam));
    const source = api.sanitiseToken(params.get(config.sourceParam), api.TOKEN_MAX_LENGTH);
    const ref = config.refParam
      ? api.sanitiseToken(params.get(config.refParam), api.TOKEN_MAX_LENGTH)
      : '';

    if (!items.length) {
      this.fail(this.copy.unreadable, false);
      return;
    }

    let snapshot = null;
    try {
      snapshot = await this.readCart();
      await this.clearCart();

      const added = [];
      const dropped = [];
      for (const item of items) {
        // Sequential, not parallel. Shopify serialises cart writes and
        // concurrent adds return spurious 422s on the same cart token.
        const ok = await this.addLine(item, sellingPlan);
        (ok ? added : dropped).push(item);
      }

      if (!added.length) {
        const restoreResult = await this.restore(snapshot);
        this.fail(this.copy.noneAvailable, restoreResult.failed > 0);
        return;
      }

      await this.writeAttributes(api.buildAttributes(config, source, ref));
      window.location.replace(api.redirectTarget(config, dropped.length));
    } catch (error) {
      console.error('Cart prefill rebuild failed:', error);
      const restoreResult = snapshot ? await this.restore(snapshot) : { attempted: 0, failed: 0 };
      this.fail(this.copy.unexpected, restoreResult.failed > 0);
    }
  }

  async readCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`cart.js returned ${response.status}`);
    const cart = await response.json();
    return (cart.items || []).map((line) => ({
      id: String(line.variant_id),
      quantity: line.quantity,
      selling_plan: line.selling_plan_allocation
        ? String(line.selling_plan_allocation.selling_plan.id)
        : null,
    }));
  }

  async clearCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart/clear.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`cart/clear.js returned ${response.status}`);
  }

  async addLine(item, sellingPlan) {
    const line = { id: item.id, quantity: item.quantity };
    if (sellingPlan) line.selling_plan = sellingPlan;

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [line] }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        console.warn('Cart prefill line dropped:', item.id, body.description || body.message || response.status);
        return false;
      }
      return true;
    } catch (error) {
      console.warn('Cart prefill line dropped:', item.id, error.message);
      return false;
    }
  }

  async restore(snapshot) {
    // Restore each line with exactly the selling plan it had before, never
    // the prefill link's requested plan. A snapshot line with no selling
    // plan was a one-time purchase; falling back to the link's plan would
    // silently convert it into a subscription on restore.
    //
    // Never throws. Callers rely on that: a caught error here must not be
    // able to trigger a second, cart-emptying restore attempt. Instead it
    // reports { attempted, failed } so the caller can tell the customer the
    // truth about what actually came back.
    const result = { attempted: 0, failed: 0 };
    if (!snapshot || !snapshot.length) return result;

    try {
      await this.clearCart();
      for (const line of snapshot) {
        result.attempted += 1;
        const ok = await this.addLine(
          { id: line.id, quantity: line.quantity },
          line.selling_plan
        );
        if (!ok) result.failed += 1;
      }
    } catch (error) {
      // clearCart failed, or something else unexpected happened before the
      // loop could run to completion. Nothing beyond what was already
      // attempted made it back into the cart.
      console.error('Cart prefill restore failed:', error);
      result.attempted = snapshot.length;
      result.failed = snapshot.length;
    }

    return result;
  }

  async writeAttributes(attributes) {
    // Attribution is not worth losing a cart over. A failed fetch here must
    // never bubble up and be mistaken for a failed rebuild.
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attributes }),
      });
      if (!response.ok) console.warn('Cart prefill attributes not written:', response.status);
    } catch (error) {
      console.warn('Cart prefill attributes not written:', error.message);
    }
  }

  fail(reason, restoreFailed) {
    const outcome = restoreFailed ? this.copy.restoreFailed : this.copy.restored;
    const message = [reason, outcome].filter(Boolean).join(' ');
    if (this.errorMessage && message) this.errorMessage.textContent = message;
    this.states.building.classList.remove('is-active');
    this.states.error.classList.add('is-active');
  }
}

customElements.define('sham-cart-prefill', ShamCartPrefill);
}
